import * as XLSX from 'xlsx';
import axios from 'axios';
import { header } from '../components/header.encry';

/**
 * VPS Excel Processor & API Service
 * 
 * This module provides utilities for processing VPS Excel/CSV files
 * and interacting with the VPS transactions API.
 * 
 * @example Usage
 * 
 * // 1. Process Excel file
 * const data = await processVpsExcelFile(file);
 * 
 * // 2. POST transactions to backend
 * await postVpsTransactions(data);
 * 
 * // 3. GET transactions with filters
 * const unprocessed = await getVpsTransactions({ is_processed: false, limit: 50 });
 * 
 * // 4. Update a single transaction
 * await updateVpsTransaction(123, {
 *   is_processed: true,
 *   lot_number: "LOT001",
 *   invoice_number: "INV001"
 * });
 * 
 * // 5. Batch mark as processed
 * await markVpsTransactionsAsProcessed([123, 124, 125], {
 *   lot_number: "LOT001",
 *   invoice_number: "INV001"
 * });
 */

/**
 * Mapping of French column names to English JSON-compliant field names
 */
const normalizeHeaderKey = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase();

const normalizeCellValue = (value) => (value === null || value === undefined ? '' : value);

const normalizeActionCode = (value) => {
    if (value === null || value === undefined || value === '') return '';

    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(Math.trunc(value)).padStart(3, '0');
    }

    const normalized = String(value).trim();
    if (!normalized) return '';
    if (/^\d+$/.test(normalized)) {
        return normalized.padStart(3, '0');
    }

    return normalized;
};

const normalizeAmount = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }

    const normalized = String(value).trim().replace(/\s/g, '').replace(',', '.');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
};

const extractVpsSummary = (workbook) => {
    const summarySheetName = (workbook.SheetNames || []).find((name) => normalizeHeaderKey(name) === normalizeHeaderKey('Summary'));
    const monthlyTotals = extractMonthlyVpsTotals(workbook);
    if (!summarySheetName) {
        return monthlyTotals;
    }

    const worksheet = workbook.Sheets[summarySheetName];
    if (!worksheet) {
        return monthlyTotals;
    }

    const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null });
    const summary = {};

    const summaryHeaderRow = rows.find((row) =>
        (row || []).some((cell) => normalizeHeaderKey(cell) === normalizeHeaderKey('NET MARCHAND'))
    );
    const netMarchandColumnIndex = summaryHeaderRow
        ? summaryHeaderRow.findIndex((cell) => normalizeHeaderKey(cell) === normalizeHeaderKey('NET MARCHAND'))
        : -1;

    if (netMarchandColumnIndex >= 0) {
        summary.netMarchand = rows.reduce((total, row) => (
            total + normalizeAmount(row?.[netMarchandColumnIndex])
        ), 0);
        summary.netMarchand = round2(summary.netMarchand);
        return {
            ...monthlyTotals,
            ...summary
        };
    }

    rows.forEach((row) => {
        const label = normalizeHeaderKey(row?.[0]);
        if (label === normalizeHeaderKey('NET MARCHAND')) {
            summary.netMarchand = normalizeAmount(row?.[1]);
        }
    });

    return {
        ...monthlyTotals,
        ...summary
    };
};

const round2 = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;
const ttcToHt = (value) => round2(normalizeAmount(value) / 1.2);
const getNormalizedHeaders = (headers = []) => headers.map(normalizeHeaderKey);
const getHeaderIndex = (headers = [], headerName) => getNormalizedHeaders(headers).indexOf(normalizeHeaderKey(headerName));
const isTransactionDataRow = (row) => row && row.length > 0 && row[0] !== '' && row[0] !== null && row[0] !== undefined;

const extractMonthlyVpsTotals = (workbook) => {
    const summary = {
        creditTransactionCount: 0,
        debitTransactionCount: 0,
        totalCreditMerchant: 0,
        totalDebitMerchant: 0,
        totalCreditAuthorisation: 0,
        totalDebitAuthorisation: 0
    };

    const addSheetTotals = (sheetName, direction) => {
        const worksheet = workbook?.Sheets?.[sheetName];
        if (!worksheet) return;

        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null });
        const headerRowIndex = findVpsHeaderRowIndex(rows);
        if (headerRowIndex < 0) return;

        const headers = rows[headerRowIndex] || [];
        const merchantCreditIndex = getHeaderIndex(headers, 'Crédit marchand');
        const merchantDebitIndex = getHeaderIndex(headers, 'Débit marchand');
        const amountIndex = getHeaderIndex(headers, 'Montant');
        const dataRows = rows.slice(headerRowIndex + 1).filter(isTransactionDataRow);

        dataRows.forEach((row) => {
            if (direction === 'credit') {
                summary.creditTransactionCount += 1;
                summary.totalCreditMerchant += normalizeAmount(row[merchantCreditIndex]);
                summary.totalCreditAuthorisation += normalizeAmount(row[amountIndex]);
            } else {
                summary.debitTransactionCount += 1;
                summary.totalDebitMerchant += normalizeAmount(row[merchantDebitIndex]);
                summary.totalDebitAuthorisation += normalizeAmount(row[amountIndex]);
            }
        });
    };

    (workbook?.SheetNames || []).filter(isMonthlyCreditSheetName).forEach((sheetName) => addSheetTotals(sheetName, 'credit'));
    (workbook?.SheetNames || []).filter(isMonthlyDebitSheetName).forEach((sheetName) => addSheetTotals(sheetName, 'debit'));

    return {
        ...summary,
        totalCreditMerchant: round2(summary.totalCreditMerchant),
        totalDebitMerchant: round2(summary.totalDebitMerchant),
        totalCreditAuthorisation: round2(summary.totalCreditAuthorisation),
        totalDebitAuthorisation: round2(summary.totalDebitAuthorisation)
    };
};
const VPS_OFLORIST_SOCID = 31;
const VPS_PRODUCT_LABELS = [
    'Chocolat',
    'Plante',
    'Pot',
    'Fleurs Coupée',
    'Composition de plantes'
];

const getRandomVpsProductLabel = () => {
    const index = Math.floor(Math.random() * VPS_PRODUCT_LABELS.length);
    return VPS_PRODUCT_LABELS[index];
};

const buildVpsLineFromTransaction = (trx) => {
    const productLabel = getRandomVpsProductLabel();
    return {
        desc: productLabel,
        description: productLabel,
        tva_tx: 20,
        currency: trx.currencyAlphaCode || 'MAD',
        qty: 1,
        subprice: ttcToHt(trx.authorisationAmount),
        product_type: 'product',
        trx: 1
    };
};

const getVpsStage = (trx = {}) => {
    if (trx.invoiceNumber || trx.invoice_number) return 'validated';
    if (trx.invoiceId || trx.invoice_id) return 'created';
    if (trx.stage) return String(trx.stage);
    if (trx.is_processed) return 'processed';
    return 'pending';
};

const isVpsEffectivelyProcessed = (trx = {}) => {
    return Boolean(
        trx.is_processed ||
        trx.invoiceId ||
        trx.invoiceNumber ||
        trx.invoice_id ||
        trx.invoice_number ||
        trx.stage
    );
};

const FIELD_MAPPING = {
    [normalizeHeaderKey('Authorisation Reference')]: 'authorisationReference',
    [normalizeHeaderKey('Message Type')]: 'messageType',
    [normalizeHeaderKey('POS Name')]: 'posName',
    [normalizeHeaderKey('Hidden Card Number')]: 'hiddenCardNumber',
    [normalizeHeaderKey('Authorisation Date')]: 'authorisationDate',
    [normalizeHeaderKey('Authorisation Amount')]: 'authorisationAmount',
    [normalizeHeaderKey('Payment Network Name')]: 'paymentNetworkName',
    [normalizeHeaderKey('Acquirer Action Code')]: 'acquirerActionCode',
    [normalizeHeaderKey('Issuer Bank Name')]: 'issuerBankName',
    [normalizeHeaderKey('Issuer Bank Abbreviation')]: 'issuerBankAbbreviation',
    [normalizeHeaderKey('PSP Name')]: 'pspName',
    [normalizeHeaderKey('Processing Code')]: 'processingCode',
    [normalizeHeaderKey('Authorisation Code')]: 'authorisationCode',
    [normalizeHeaderKey('Transaction Matching Status')]: 'transactionMatchingStatus',
    [normalizeHeaderKey('Currency Alpha Code')]: 'currencyAlphaCode',
    [normalizeHeaderKey('Token Serial Card')]: 'tokenSerialCard',
    [normalizeHeaderKey('Issuer Bin')]: 'issuerBin',
    [normalizeHeaderKey('Issuer Action Code')]: 'issuerActionCode',
    [normalizeHeaderKey('Acquirer Country')]: 'acquirerCountry',
    [normalizeHeaderKey('STAN')]: 'stan',
    [normalizeHeaderKey('Authorisation Request DateTime')]: 'authorisationRequestDateTime',
    [normalizeHeaderKey('Created At')]: 'createdAt'
};

const C1_FIELD_MAPPING = {
    [normalizeHeaderKey('Date')]: 'authorisationDate',
    [normalizeHeaderKey('Stan')]: 'stan',
    [normalizeHeaderKey('Operation')]: 'messageType',
    [normalizeHeaderKey('Systems*')]: 'paymentNetworkName',
    [normalizeHeaderKey('Types*')]: 'transactionType',
    [normalizeHeaderKey('Carte')]: 'hiddenCardNumber',
    [normalizeHeaderKey('Montant')]: 'authorisationAmount',
    [normalizeHeaderKey('Com%')]: 'commissionRate',
    [normalizeHeaderKey('Com HT')]: 'commission',
    [normalizeHeaderKey('Com TTC*')]: 'commissionWithTax',
    [normalizeHeaderKey('Crédit/Débit')]: 'merchantCredit'
};

/**
 * Convert Excel serial date to DD/MM/YYYY format
 * Excel uses 1899-12-30 as day 0 (base date)
 * Example: 46357.999768518515 → 20/02/2026
 * 
 * @param {number} serial - Excel serial date number
 * @returns {string} - Date in DD/MM/YYYY format
 */
const excelSerialToDate = (serial) => {
    if (serial === null || serial === undefined || serial === '') return '';
    if (typeof serial !== 'number') return '';
    // Excel epoch: 1899-12-30
    const excelEpoch = new Date(Date.UTC(1899, 11, 30));
    // Convert days to milliseconds
    const milliseconds = serial * 24 * 60 * 60 * 1000;
    const date_info = new Date(excelEpoch.getTime() + milliseconds);
    
    const day = String(date_info.getUTCDate()).padStart(2, '0');
    const month = String(date_info.getUTCMonth() + 1).padStart(2, '0');
    const year = date_info.getUTCFullYear();
    
    return `${day}/${month}/${year}`;
};

/**
 * Convert Excel serial DateTime to time HH:MM:SS format
 * Example: 46357.999768518515 → 23:59:40
 * Decimal part (0.999768518515) × 24h = 23h 59m 40s
 * 
 * @param {number} serial - Excel serial DateTime number
 * @returns {string} - Time in HH:MM:SS format
 */
const excelSerialToTime = (serial) => {
    if (serial === null || serial === undefined || serial === '') return '';
    if (typeof serial !== 'number') return '';
    // Excel epoch: 1899-12-30
    const excelEpoch = new Date(Date.UTC(1899, 11, 30));
    // Convert days to milliseconds
    const milliseconds = serial * 24 * 60 * 60 * 1000;
    const date_info = new Date(excelEpoch.getTime() + milliseconds);
    
    const hours = String(date_info.getUTCHours()).padStart(2, '0');
    const minutes = String(date_info.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date_info.getUTCSeconds()).padStart(2, '0');
    
    return `${hours}:${minutes}:${seconds}`;
};

const isC1VpsFile = (headers) => {
    const normalizedHeaders = (headers || []).map(normalizeHeaderKey);
    const requiredBaseHeaders = [
        'Date',
        'Stan',
        'Operation',
        'Systems*',
        'Types*',
        'Carte',
        'Montant',
        'Com%',
        'Com HT',
        'Com TTC*'
    ].map(normalizeHeaderKey);
    const merchantAmountHeaders = [
        'Crédit/Débit',
        'CrÃ©dit/DÃ©bit',
        'Crédit marchand',
        'CrÃ©dit marchand',
        'Débit marchand'
    ].map(normalizeHeaderKey);

    merchantAmountHeaders.push('credit/debit', 'credit marchand', 'debit marchand');

    return requiredBaseHeaders.every(header => normalizedHeaders.includes(header)) &&
        merchantAmountHeaders.some(header => normalizedHeaders.includes(header));
};

const buildStandardVpsRecord = (headers, row) => {
    const record = {};

    headers.forEach((header, index) => {
        const englishFieldName = FIELD_MAPPING[normalizeHeaderKey(header)] || header;
        let value = normalizeCellValue(row[index]);

        if (typeof value === 'string' && value.startsWith("'")) {
            value = value.substring(1);
        }

        if (englishFieldName === 'acquirerActionCode' || englishFieldName === 'issuerActionCode') {
            value = normalizeActionCode(value);
        }

        record[englishFieldName] = value;
    });

    return record;
};

const buildC1VpsRecord = (headers, row, metadata = {}) => {
    const transactionDirection = metadata.transaction_direction || metadata.transactionDirection || 'credit';
    const record = {
        acquirerActionCode: '000',
        issuerActionCode: '000',
        currencyAlphaCode: 'MAD',
        paymentNetworkName: 'VPS',
        transactionDirection,
        transaction_direction: transactionDirection,
        ...metadata
    };

    headers.forEach((header, index) => {
        const normalizedHeader = normalizeHeaderKey(header);
        let englishFieldName = C1_FIELD_MAPPING[normalizedHeader];
        if (!englishFieldName && normalizedHeader === 'credit marchand') {
            englishFieldName = 'merchantCredit';
        }
        if (!englishFieldName && normalizedHeader === 'debit marchand') {
            englishFieldName = 'merchantDebit';
        }
        if (!englishFieldName && [
            normalizeHeaderKey('Crédit marchand'),
            normalizeHeaderKey('CrÃ©dit marchand')
        ].includes(normalizedHeader)) {
            englishFieldName = 'merchantCredit';
        }
        if (!englishFieldName && normalizedHeader === normalizeHeaderKey('Débit marchand')) {
            englishFieldName = 'merchantDebit';
        }
        if (!englishFieldName) return;

        let value = normalizeCellValue(row[index]);

        if (englishFieldName === 'authorisationDate') {
            value = typeof value === 'number' ? value : normalizeAmount(value);
        } else if (englishFieldName === 'authorisationAmount' || englishFieldName === 'commissionRate' || englishFieldName === 'commission' || englishFieldName === 'commissionWithTax' || englishFieldName === 'merchantCredit' || englishFieldName === 'merchantDebit') {
            value = normalizeAmount(value);
        } else if (typeof value === 'string' && value.startsWith("'")) {
            value = value.substring(1);
        }

        record[englishFieldName] = value;
    });

    record.authorisationReference = record.authorisationReference || record.stan || '';
    record.posName = record.posName || String(record.paymentNetworkName || 'VPS');
    record.hiddenCardNumber = record.hiddenCardNumber || '';
    record.paymentNetworkName = record.paymentNetworkName || 'VPS';
    record.authorisationDate = normalizeAmount(record.authorisationDate);
    record.authorisationAmount = normalizeAmount(record.authorisationAmount);
    record.authorisationAmountHt = ttcToHt(record.authorisationAmount);
    record.commission = normalizeAmount(record.commission);
    record.tvaCommission = Math.max(0, normalizeAmount(record.commissionWithTax) - normalizeAmount(record.commission));
    record.acquirerActionCode = '000';
    record.issuerActionCode = '000';

    return record;
};

const extractMonthlyCreditRecords = (workbook) => {
    return extractMonthlyRecordsByDirection(workbook, {
        sheetFilter: isMonthlyCreditSheetName,
        direction: 'credit'
    });
};

const extractMonthlyDebitRecords = (workbook) => {
    return extractMonthlyRecordsByDirection(workbook, {
        sheetFilter: isMonthlyDebitSheetName,
        direction: 'debit'
    });
};

const extractMonthlyRecordsByDirection = (workbook, { sheetFilter, direction }) => {
    const monthlyWorksheets = getMonthlyWorksheets(workbook, sheetFilter);
    const records = [];

    monthlyWorksheets.forEach(({ sheetName, worksheet }) => {
        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null });
        const headerRowIndex = findVpsHeaderRowIndex(rows);
        if (headerRowIndex < 0) return;

        const headers = rows[headerRowIndex] || [];
        if (!isC1VpsFile(headers)) return;

        const sourceSheetDate = extractDateFromMonthlySheetName(sheetName);
        rows
            .slice(headerRowIndex + 1)
            .filter(isTransactionDataRow)
            .forEach((row, rowIndex) => {
                records.push(buildC1VpsRecord(headers, row, {
                    sourceSheetName: sheetName,
                    sourceSheetDate,
                    sourceSheetRow: headerRowIndex + rowIndex + 2,
                    transactionDirection: direction,
                    transaction_direction: direction
                }));
            });
    });

    return records;
};

const detectVpsWorksheet = (workbook) => {
    for (const sheetName of workbook.SheetNames || []) {
        const worksheet = workbook.Sheets[sheetName];
        if (!worksheet) continue;

        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        const headers = rows[0] || [];

        if (isC1VpsFile(headers)) {
            return worksheet;
        }

        if (isVpsFile(headers)) {
            return worksheet;
        }
    }

    return null;
};

const findVpsHeaderRowIndex = (rows) => {
    return (rows || []).findIndex((row) => isC1VpsFile(row || []) || isVpsFile(row || []));
};

const isMonthlyCreditSheetName = (sheetName) => /-\s*C$/i.test(String(sheetName || '').trim());
const isMonthlyDebitSheetName = (sheetName) => /-\s*D$/i.test(String(sheetName || '').trim());
const extractDateFromMonthlySheetName = (sheetName) => {
    const match = String(sheetName || '').trim().match(/^(\d{2})-(\d{2})-(\d{4})-[CD]$/i);
    if (!match) return '';
    return `${match[1]}/${match[2]}/${match[3]}`;
};

const getMonthlyWorksheets = (workbook, sheetFilter) => {
    return (workbook.SheetNames || [])
        .filter(sheetFilter)
        .map((sheetName) => ({
            sheetName,
            worksheet: workbook.Sheets[sheetName]
        }))
        .filter((entry) => entry.worksheet);
};

/**
 * Group VPS transactions by date (similar to CMI grouping by remise)
 * @param {Array} transactions - Array of VPS transactions from backend response
 * @returns {Array} - Array of grouped factures compatible with CMI display format
 */
export const groupVpsTransactionsByDate = (transactions) => {
    const grouped = {};
    const buildRawVpsTransaction = (trx, dateKey) => ({
        dateTransaction: dateKey,
        heure: trx.authorisationDate ? excelSerialToTime(trx.authorisationDate) : '',
        numeroCarte: trx.hiddenCardNumber,
        systeme: trx.paymentNetworkName,
        montantBrutTTC: parseFloat(trx.authorisationAmount || 0),
        montantNetTTC: parseFloat(trx.merchantCredit ?? trx.merchant_credit ?? trx.authorisationAmount ?? 0),
        commission: parseFloat(trx.commission ?? trx.commission_ht ?? 0),
        tvaCommission: parseFloat(trx.tvaCommission || 0),
        numeroAutorisation: trx.authorisationReference,
        stan: trx.stan,
        devise: trx.currencyAlphaCode || 'MAD',
        issuerBank: trx.issuerBankName,
        acquirerActionCode: trx.acquirerActionCode,
        issuerActionCode: trx.issuerActionCode,
        sourceSheetName: trx.sourceSheetName,
        sourceSheetDate: trx.sourceSheetDate,
        sourceSheetRow: trx.sourceSheetRow,
        transactionId: trx._id || trx.id
    });
    
    // Filter transactions where both action codes equal "000"
    const validTransactions = transactions.filter(trx => 
        normalizeActionCode(trx.acquirerActionCode) === '000' &&
        normalizeActionCode(trx.issuerActionCode) === '000' &&
        String(trx.transaction_direction || trx.transactionDirection || 'credit').toLowerCase() !== 'debit'
    );
    
    validTransactions.forEach(trx => {
        // Convert Excel serial date to readable format
        const dateKey = trx.sourceSheetDate || excelSerialToDate(trx.authorisationDate);
        const groupKey = `VPS_${dateKey.replace(/\//g, '')}`;
        const noteLabel = `VPS - ${dateKey}`;
        
        if (grouped[groupKey]) {
            // Keep one Dolibarr line per VPS record
            grouped[groupKey].lines.push(buildVpsLineFromTransaction(trx));
            grouped[groupKey].rawTransactions.push(buildRawVpsTransaction(trx, dateKey));
        } else {
            // Create new group
            grouped[groupKey] = {
                socid: VPS_OFLORIST_SOCID,
                remiseId: groupKey,
                company: 'vps',
                datef: dateKey,
                note_private: noteLabel,
                sourceSheetName: trx.sourceSheetName,
                rawTransactions: [buildRawVpsTransaction(trx, dateKey)],
                lines: [buildVpsLineFromTransaction(trx)],
                processed: trx.is_processed || false,
                invoiceId: trx.invoice_id || trx.invoiceId || null,
                invoiceNumber: trx.invoice_number || trx.invoiceNumber || null,
                stage: getVpsStage(trx),
                baseSocid: VPS_OFLORIST_SOCID,
                isVps: true // Flag to identify VPS transactions
            };
        }
    });
    
    return Object.values(grouped);
};

export const groupVpsDebitTransactionsByTable = (transactions) => {
    const grouped = {};
    const debitTransactions = (transactions || []).filter(trx =>
        String(trx.transaction_direction || trx.transactionDirection || '').toLowerCase() === 'debit'
    );

    debitTransactions.forEach((trx) => {
        const dateKey = trx.sourceSheetDate || excelSerialToDate(trx.authorisationDate);
        const groupKey = trx.sourceSheetName
            ? `VPS_D_${String(trx.sourceSheetName).replace(/\s+/g, '_')}`
            : `VPS_D_${dateKey.replace(/\//g, '')}`;
        const amount = normalizeAmount(trx.merchantDebit ?? trx.merchant_debit ?? trx.authorisationAmount);

        if (!grouped[groupKey]) {
            grouped[groupKey] = {
                remiseId: groupKey,
                note_private: trx.sourceSheetName ? `VPS D - ${trx.sourceSheetName}` : `VPS D - ${dateKey}`,
                datef: dateKey,
                sourceSheetName: trx.sourceSheetName,
                totalDebit: 0,
                transactionCount: 0,
                rawTransactions: [],
                isVpsDebit: true
            };
        }

        grouped[groupKey].totalDebit = round2(grouped[groupKey].totalDebit + amount);
        grouped[groupKey].transactionCount += 1;
        grouped[groupKey].rawTransactions.push({
            dateTransaction: dateKey,
            heure: trx.authorisationDate ? excelSerialToTime(trx.authorisationDate) : '',
            numeroCarte: trx.hiddenCardNumber,
            systeme: trx.paymentNetworkName,
            montantBrutTTC: normalizeAmount(trx.authorisationAmount),
            montantDebitTTC: amount,
            commission: normalizeAmount(trx.commission ?? trx.commission_ht),
            numeroAutorisation: trx.authorisationReference,
            stan: trx.stan,
            devise: trx.currencyAlphaCode || 'MAD',
            sourceSheetName: trx.sourceSheetName,
            sourceSheetDate: trx.sourceSheetDate,
            sourceSheetRow: trx.sourceSheetRow,
            transactionId: trx._id || trx.id,
            matchedCreditTransactionId: trx.matched_credit_transaction_id,
            refundInvoiceId: trx.refund_invoice_id,
            refundInvoiceNumber: trx.refund_invoice_number,
            refundStatus: trx.refund_status
        });
    });

    return Object.values(grouped);
};

/**
 * Process VPS Excel/CSV file and return formatted data
 * @param {File} file - The Excel or CSV file to process
 * @returns {Promise<Array>} - Array of processed VPS transactions
 */
export const processVpsExcelFile = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const monthlyCreditRecords = extractMonthlyCreditRecords(workbook);
                const monthlyDebitRecords = extractMonthlyDebitRecords(workbook);
                let transformedData = monthlyCreditRecords;

                if (monthlyCreditRecords.length === 0) {
                const worksheet = detectVpsWorksheet(workbook);

                if (!worksheet) {
                    reject(new Error('Format de fichier VPS invalide. Vérifiez que le fichier contient bien les en-têtes VPS attendues ou la table C-1 attendue.'));
                    return;
                }

                const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                if (rows.length < 2) {
                    reject(new Error('Le fichier est vide ou ne contient pas de données'));
                    return;
                }

                const headers = rows[0];
                const dataRows = rows.slice(1);

                if (isC1VpsFile(headers)) {
                    transformedData = dataRows
                        .filter(row => row && row.length > 0 && row[0] !== '' && row[0] !== null && row[0] !== undefined)
                        .map(row => buildC1VpsRecord(headers, row));
                } else if (isVpsFile(headers)) {
                    transformedData = dataRows
                        .filter(row => row && row.length > 0) // Filter out empty rows
                        .map(row => buildStandardVpsRecord(headers, row))
                        .filter(record => {
                            // Only include transactions where both action codes equal "000"
                            return normalizeActionCode(record.acquirerActionCode) === '000' &&
                                normalizeActionCode(record.issuerActionCode) === '000';
                        });
                } else {
                    reject(new Error('Format de fichier VPS invalide. Vérifiez que le fichier contient bien les en-têtes VPS attendues ou la table C-1 attendue.'));
                    return;
                }

                }

                transformedData = transformedData.map((record) => ({
                    ...record,
                    stage: getVpsStage(record),
                    is_processed: isVpsEffectivelyProcessed(record),
                    invoiceId: record.invoiceId || record.invoice_number || null,
                    invoiceNumber: record.invoiceNumber || record.invoice_number || null
                }));

                transformedData.vpsSummary = extractVpsSummary(workbook);
                transformedData.vpsDebitTransactions = monthlyDebitRecords.map((record) => ({
                    ...record,
                    stage: getVpsStage(record),
                    is_processed: isVpsEffectivelyProcessed(record),
                    invoiceId: record.invoiceId || record.invoice_number || null,
                    invoiceNumber: record.invoiceNumber || record.invoice_number || null
                }));

                resolve(transformedData);
            } catch (error) {
                console.error('Error processing VPS file:', error);
                reject(new Error('Erreur lors du traitement du fichier: ' + error.message));
            }
        };

        reader.onerror = (error) => {
            reject(new Error('Erreur lors de la lecture du fichier: ' + error.message));
        };

        reader.readAsArrayBuffer(file);
    });
};

/**
 * Validates if a file appears to be a VPS file based on headers
 * @param {Array} headers - Array of header column names
 * @returns {boolean} - True if headers match VPS format
 */
export const isVpsFile = (headers) => {
    const requiredHeaders = [
        'Authorisation Reference',
        'POS Name',
        'Authorisation Date',
        'Authorisation Amount',
        'Payment Network Name'
    ].map(normalizeHeaderKey);

    const normalizedHeaders = (headers || []).map(normalizeHeaderKey);

    return requiredHeaders.every(header => normalizedHeaders.includes(header)) || isC1VpsFile(headers);
};

/**
 * POST VPS transactions to the backend
 * @param {Array} transactions - Array of VPS transaction objects
 * @returns {Promise<Object>} - API response
 */
export const postVpsTransactions = async (transactions) => {
    try {
        const response = await axios.post(
            process.env.REACT_APP_PEGASUS_API_URL + "/accounting/data/entry",
            {
                type: 'vps',
                data: transactions
            },
            header()
        );
        return response.data;
    } catch (error) {
        console.error('Error posting VPS transactions:', error);
        throw new Error('Erreur lors de l\'envoi des transactions VPS: ' + (error.response?.data?.message || error.message));
    }
};

/**
 * GET VPS transactions from the backend with optional filters
 * @param {Object} filters - Optional filters (is_processed, lot_number, limit, offset, etc.)
 * @returns {Promise<Array>} - Array of VPS transactions
 */
export const getVpsTransactions = async (filters = {}) => {
    try {
        const params = new URLSearchParams();
        
        // Add filters to query params
        if (filters.is_processed !== undefined) {
            params.append('is_processed', filters.is_processed);
        }
        if (filters.lot_number) {
            params.append('lot_number', filters.lot_number);
        }
        if (filters.invoice_number) {
            params.append('invoice_number', filters.invoice_number);
        }
        if (filters.limit) {
            params.append('limit', filters.limit);
        }
        if (filters.offset) {
            params.append('offset', filters.offset);
        }
        if (filters.start_date) {
            params.append('start_date', filters.start_date);
        }
        if (filters.end_date) {
            params.append('end_date', filters.end_date);
        }

        const queryString = params.toString();
        const url = `${process.env.REACT_APP_PEGASUS_API_URL}/accounting/data/entry${queryString ? '?' + queryString : ''}`;
        
        const response = await axios.get(url, header());
        return response.data;
    } catch (error) {
        console.error('Error fetching VPS transactions:', error);
        throw new Error('Erreur lors de la récupération des transactions VPS: ' + (error.response?.data?.message || error.message));
    }
};

/**
 * PATCH (update) a VPS transaction's processing information
 * @param {string|number} transactionId - Transaction ID
 * @param {Object} updates - Updates to apply (is_processed, processing_date, lot_number, invoice_number)
 * @returns {Promise<Object>} - Updated transaction
 */
export const updateVpsTransaction = async (transactionId, updates) => {
    try {
        const response = await axios.patch(
            `${process.env.REACT_APP_PEGASUS_API_URL}/accounting/data/entry/${transactionId}`,
            updates,
            header()
        );
        return response.data;
    } catch (error) {
        console.error('Error updating VPS transaction:', error);
        throw new Error('Erreur lors de la mise à jour de la transaction VPS: ' + (error.response?.data?.message || error.message));
    }
};

/**
 * Batch update multiple VPS transactions
 * @param {Array} transactionIds - Array of transaction IDs
 * @param {Object} updates - Updates to apply to all transactions
 * @returns {Promise<Array>} - Array of update results
 */
export const batchUpdateVpsTransactions = async (transactionIds, updates) => {
    try {
        const updatePromises = transactionIds.map(id => 
            updateVpsTransaction(id, updates)
        );
        return await Promise.all(updatePromises);
    } catch (error) {
        console.error('Error batch updating VPS transactions:', error);
        throw new Error('Erreur lors de la mise à jour groupée des transactions VPS: ' + error.message);
    }
};

/**
 * Mark VPS transactions as processed
 * @param {Array} transactionIds - Array of transaction IDs
 * @param {Object} processingInfo - Processing information (lot_number, invoice_number)
 * @returns {Promise<Array>} - Array of update results
 */
export const markVpsTransactionsAsProcessed = async (transactionIds, processingInfo = {}) => {
    const updates = {
        is_processed: true,
        processing_date: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
        ...processingInfo
    };
    
    return await batchUpdateVpsTransactions(transactionIds, updates);
};

